import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const appendQueryParams = (queryParams, params = {}) => {
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== "" && item != null && item !== undefined) {
          queryParams.append(`${key}[]`, String(item));
        }
      });
      return;
    }
    if (value !== "" && value != null && value !== undefined) {
      queryParams.append(key, String(value));
    }
  });
};

const itemTaxableFormValue = (item) => {
  if (item?.taxable === true || item?.taxable === 1 || item?.taxable === "1") {
    return "1";
  }
  return "0";
};

const authMultipartHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "multipart/form-data",
  Lang: i18n.language,
});

/** Build multipart body for POST /debit-notes/manual */
export function buildManualDebitNoteFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  [
    "provider_type",
    "provider_id",
    "winch_branch_id",
    "responsible_name",
    "issue_date",
    "due_at",
    "notification_type",
    "tax_calculation",
    "account_id",
    "purchase_invoice_id",
    "description",
    "notes",
    "comment",
    "status",
  ].forEach((key) => append(key, form[key]));

  (form.items || []).forEach((item, index) => {
    const isCompensation = Boolean(item?.is_compensation);
    [
      "supply_date",
      "description",
      "from_city_id",
      "to_city_id",
      "quantity",
      "unit_price",
      "tax_percentage",
    ].forEach((field) => {
      const value = item[field];
      if (value !== "" && value != null) {
        fd.append(`items[${index}][${field}]`, String(value));
      } else if (field === "from_city_id" || field === "to_city_id") {
        fd.append(`items[${index}][${field}]`, "");
      }
    });

    if (isCompensation) {
      const categoryId =
        item.compensation_category_id || item.service_type_id || "";
      if (categoryId !== "" && categoryId != null) {
        fd.append(
          `items[${index}][compensation_category_id]`,
          String(categoryId)
        );
      }
    } else if (item.service_type_id !== "" && item.service_type_id != null) {
      fd.append(
        `items[${index}][service_type_id]`,
        String(item.service_type_id)
      );
    }

    fd.append(`items[${index}][taxable]`, itemTaxableFormValue(item));
  });

  return fd;
}

/** Build multipart body for POST /debit-notes/full */
export function buildFullDebitNoteFormData({
  purchase_invoice_id,
  issue_date,
  status,
}) {
  const fd = new FormData();
  fd.append("purchase_invoice_id", String(purchase_invoice_id));
  fd.append("issue_date", String(issue_date));
  fd.append("status", String(status));
  return fd;
}

/** Build multipart body for POST /debit-notes/partial */
export function buildPartialDebitNoteFormData({
  purchase_invoice_id,
  invoice_item_ids,
  issue_date,
  status,
}) {
  const fd = new FormData();
  fd.append("purchase_invoice_id", String(purchase_invoice_id));
  fd.append("issue_date", String(issue_date));
  fd.append("status", String(status));
  (invoice_item_ids || []).forEach((id) => {
    if (id !== "" && id != null) {
      fd.append("invoice_item_ids[]", String(id));
    }
  });
  return fd;
}

export const fetchDebitNotes = createAsyncThunk(
  "debit_notes/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      appendQueryParams(queryParams, { page: 1, per_page: 20, ...params });
      const qs = queryParams.toString();
      const response = await axios.get(
        `${BASE_URL}/debit-notes${qs ? `?${qs}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

export const fetchDebitNoteDetail = createAsyncThunk(
  "debit_notes/fetchDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/debit-notes/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data?.data ?? null;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to load debit note";
      return rejectWithValue(message);
    }
  }
);

export const createManualDebitNote = createAsyncThunk(
  "debit_notes/createManual",
  async (data, { rejectWithValue }) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axios.post(
        `${BASE_URL}/debit-notes/manual`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...(isFormData
              ? { "Content-Type": "multipart/form-data" }
              : { "Content-Type": "application/json" }),
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create debit note";
      return rejectWithValue(message);
    }
  }
);

export const createFullDebitNote = createAsyncThunk(
  "debit_notes/createFull",
  async (payload, { rejectWithValue }) => {
    try {
      const data =
        typeof FormData !== "undefined" && payload instanceof FormData
          ? payload
          : buildFullDebitNoteFormData(payload);
      const response = await axios.post(`${BASE_URL}/debit-notes/full`, data, {
        headers: authMultipartHeaders(),
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create full debit note";
      return rejectWithValue(message);
    }
  }
);

export const createPartialDebitNote = createAsyncThunk(
  "debit_notes/createPartial",
  async (payload, { rejectWithValue }) => {
    try {
      const data =
        typeof FormData !== "undefined" && payload instanceof FormData
          ? payload
          : buildPartialDebitNoteFormData(payload);
      const response = await axios.post(
        `${BASE_URL}/debit-notes/partial`,
        data,
        {
          headers: authMultipartHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to create partial debit note";
      return rejectWithValue(message);
    }
  }
);

/** Coerce order_id to a plain integer string (never JSON array). */
const normalizeOrderIdForApi = (raw) => {
  if (raw === "" || raw == null) return "";
  if (Array.isArray(raw)) {
    const first = raw.find((v) => v !== "" && v != null);
    return first != null ? String(first).replace(/\D/g, "") : "";
  }
  let s = String(raw).trim();
  if (s.startsWith("[")) {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        const first = parsed.find((v) => v !== "" && v != null);
        return first != null ? String(first).replace(/\D/g, "") : "";
      }
    } catch {
      /* fall through */
    }
  }
  return s.replace(/\D/g, "");
};

/**
 * Build multipart body for POST /debit-notes/{id}/items (draft update).
 * Always sends (as string FormData fields):
 *   items[i][order_id], items[i][quantity], items[i][unit_price]
 * Optional:
 *   items[i][description], items[i][compensation_id]
 */
export function buildUpdateDebitNoteItemsFormData(form) {
  const fd = new FormData();

  (form.items || []).forEach((item, index) => {
    const orderId = normalizeOrderIdForApi(
      item?.order_id ?? item?.trip_request_id ?? item?.request_id
    );
    const compensationId = normalizeOrderIdForApi(item?.compensation_id);
    const quantity =
      item?.quantity !== "" && item?.quantity != null
        ? String(item.quantity).replace(/[^\d.]/g, "")
        : "";
    const unitPrice =
      item?.unit_price !== "" && item?.unit_price != null
        ? String(item.unit_price).replace(/[^\d.]/g, "")
        : "";
    const description =
      item?.description != null ? String(item.description).trim() : "";

    // Required payload keys for each row (plain strings, never JSON arrays)
    fd.append(`items[${index}][order_id]`, orderId);
    fd.append(`items[${index}][quantity]`, quantity);
    fd.append(`items[${index}][unit_price]`, unitPrice);

    if (description) {
      fd.append(`items[${index}][description]`, description);
    }
    if (compensationId) {
      fd.append(`items[${index}][compensation_id]`, compensationId);
    }
  });

  return fd;
}

export const updateDebitNoteItems = createAsyncThunk(
  "debit_notes/updateItems",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const data =
        typeof FormData !== "undefined" && formData instanceof FormData
          ? formData
          : buildUpdateDebitNoteItemsFormData(formData);
      const response = await axios.post(
        `${BASE_URL}/debit-notes/${id}/items`,
        data,
        {
          headers: authMultipartHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update debit note items";
      return rejectWithValue(message);
    }
  }
);

/** POST /debit-notes/publish — body: id (string) */
export const publishDebitNote = createAsyncThunk(
  "debit_notes/publish",
  async (noteId, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("id", String(noteId));

      const response = await axios.post(
        `${BASE_URL}/debit-notes/publish`,
        formData,
        {
          headers: authMultipartHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to publish debit note";
      return rejectWithValue(message);
    }
  }
);

const DebitNotesSlice = createSlice({
  name: "debit_notes",
  initialState: {
    debit_notes: [],
    record: null,
    isLoading: false,
    detailLoading: false,
    detailError: null,
    updateLoading: false,
    updateSuccess: null,
    publishLoading: false,
    error: null,
    success: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.updateSuccess = null;
    },
    clearDebitNoteDetail: (state) => {
      state.record = null;
      state.detailLoading = false;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDebitNotes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDebitNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.debit_notes = action.payload?.data?.data || [];
        state.pagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchDebitNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
        state.debit_notes = [];
        state.pagination = null;
      })
      .addCase(fetchDebitNoteDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchDebitNoteDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchDebitNoteDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload || "Failed to load debit note";
      })
      .addCase(createManualDebitNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createManualDebitNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
      })
      .addCase(createManualDebitNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create debit note";
      })
      .addCase(createFullDebitNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createFullDebitNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
      })
      .addCase(createFullDebitNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create full debit note";
      })
      .addCase(createPartialDebitNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createPartialDebitNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
      })
      .addCase(createPartialDebitNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to create partial debit note";
      })
      .addCase(updateDebitNoteItems.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.updateSuccess = null;
      })
      .addCase(updateDebitNoteItems.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess =
          action.payload?.message || "Updated successfully";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(updateDebitNoteItems.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "Failed to update debit note items";
      })
      .addCase(publishDebitNote.pending, (state) => {
        state.publishLoading = true;
        state.error = null;
      })
      .addCase(publishDebitNote.fulfilled, (state, action) => {
        state.publishLoading = false;
        state.success = action.payload?.message || "Published successfully";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(publishDebitNote.rejected, (state, action) => {
        state.publishLoading = false;
        state.error = action.payload || "Failed to publish debit note";
      });
  },
});

export const { clearState, clearDebitNoteDetail } = DebitNotesSlice.actions;
export default DebitNotesSlice.reducer;
